import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  makeStyles,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import {
  ContentEmailType,
  ContentSelectorInput,
  GroupSuggestionFragment,
  useGetGroupQuery,
} from 'api';
import { DeleteIcon } from 'components';
import { getContentUrlRegex } from 'helpers';
import { useProtectedClient, useNotify } from 'hooks';
import { ContentEmailValues } from 'features/admin/types';

import SuggestGroup from './SuggestGroup';

interface IProps {
  index: number;
}

const useStyles = makeStyles(theme => ({
  tableRowRoot: {
    '&:nth-child(even)': {
      background: 'transparent',
    },
  },
  iconButton: {
    padding: 0,
  },
  checkIcon: {
    marginRight: theme.spacing(3),
  },
  blank: {
    color: 'transparent',
  },
}));

const ContentRow: FC<IProps> = ({ index }) => {
  const classes = useStyles();
  const client = useProtectedClient();
  const { t } = useTranslation();

  const [featuredContentValue, setFeaturedContentValue] = useState('');
  const [groupValue, setGroupValue] = useState<GroupSuggestionFragment>();
  const [validationError, setValidationError] = useState('');
  const notify = useNotify();

  const { touched, errors, values, setFieldValue } = useFormikContext<ContentEmailValues>();
  const { content_specs } = values;

  const position = index + 1;
  const contentType = getContentType();
  const value = getId();

  useGetGroupQuery(
    { id: getId() },
    {
      enabled: contentType === ContentEmailType.Group,
      onSuccess: next => {
        setGroupValue(next.getGroup);
      },
    }
  );

  function getRowError() {
    const isSpecsTouched = touched.content_specs;

    if (!isSpecsTouched) {
      return null;
    }

    const isRowTouched = touched.content_specs && touched.content_specs[index];
    const currentError =
      errors.content_specs && (errors.content_specs[index] as ContentSelectorInput);

    return isRowTouched ? currentError && currentError.id : null;
  }

  function getSpec() {
    return content_specs[index];
  }

  function getContentType() {
    const spec = getSpec();
    return (spec && spec.content_type) || '';
  }

  function getId() {
    const spec = getSpec();
    return (spec && spec.id) || '';
  }

  function handleClear() {
    setGroupValue(undefined);
    setFeaturedContentValue('');
    setFieldValue(`content_specs[${index}]`, undefined);
  }

  function handleUrlValidation() {
    const hasEntry = !!featuredContentValue;
    const isValidUrl = getContentUrlRegex(client).test(featuredContentValue);

    if (!hasEntry) {
      setValidationError(t('contentEmails:contentUrlRequired'));
      return notify.error({ message: t('contentEmails:contentUrlRequired') });
    }

    if (!isValidUrl) {
      setValidationError(t('contentEmails:contentUrlMismatch'));
      return notify.error({ message: t('contentEmails:contentUrlMismatch') });
    }

    setFieldValue(`content_specs[${index}].id`, featuredContentValue);
  }

  function renderClearCell() {
    return (
      <TableCell padding="none">
        <IconButton className={classes.iconButton} onClick={handleClear}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    );
  }

  function renderFeaturedSourceAdornment() {
    return (
      <IconButton onClick={handleUrlValidation}>
        <DeleteIcon iconProps={{ className: featuredContentValue ? '' : classes.blank }} />
      </IconButton>
    );
  }

  function renderFeaturedSource() {
    const rowError = getRowError();

    if (value) {
      return (
        <Fragment>
          <TableCell>
            <TextField
              fullWidth
              disabled
              value={value}
              InputProps={{
                endAdornment: <CheckCircleIcon className={classes.checkIcon} color="primary" />,
              }}
            />
          </TableCell>
          {renderClearCell()}
        </Fragment>
      );
    }

    return (
      <Fragment>
        <TableCell>
          <TextField
            fullWidth
            variant="outlined"
            value={featuredContentValue}
            placeholder={t('contentEmails:contentUrl')}
            onChange={event => setFeaturedContentValue(event.target.value)}
            error={!!validationError || !!rowError}
            helperText={validationError || rowError}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                handleUrlValidation();
              }
            }}
            InputProps={{
              endAdornment: renderFeaturedSourceAdornment(),
            }}
          />
        </TableCell>
        {renderClearCell()}
      </Fragment>
    );
  }

  function renderGroupSource() {
    const rowError = getRowError();

    return (
      <Fragment>
        <TableCell colSpan={1}>
          <SuggestGroup
            value={groupValue}
            placeholder={t('contentEmails:contentGroup')}
            validationError={validationError || rowError}
            onChange={group => {
              setGroupValue(group);
              setFieldValue(`content_specs[${index}].id`, group.id);
            }}
          />
        </TableCell>
        {renderClearCell()}
      </Fragment>
    );
  }

  function renderUserSource() {
    return (
      <Fragment>
        <TableCell>
          <TextField fullWidth disabled value={t('contentEmails:contentTimeline')} />
        </TableCell>
        {renderClearCell()}
      </Fragment>
    );
  }

  function renderSource() {
    if (contentType === ContentEmailType.Timeline) {
      return renderUserSource();
    }

    if (contentType === ContentEmailType.Featured) {
      return renderFeaturedSource();
    }

    if (contentType === ContentEmailType.Group) {
      return renderGroupSource();
    }

    return <TableCell colSpan={2} />;
  }

  return (
    <TableRow classes={{ root: classes.tableRowRoot }} key={position}>
      <TableCell align="center">
        <Typography variant="h5" color="textSecondary">
          {position}
        </Typography>
      </TableCell>
      <TableCell>
        <Select
          disabled={!!contentType}
          displayEmpty
          fullWidth
          value={contentType}
          onChange={event =>
            setFieldValue(`content_specs[${index}].content_type`, event.target.value)
          }
        >
          <MenuItem value="">{t('common:select')}</MenuItem>
          <MenuItem value={ContentEmailType.Featured}>{t('common:featured')}</MenuItem>
          <MenuItem value={ContentEmailType.Group}>{t('common:group')}</MenuItem>
          <MenuItem value={ContentEmailType.Timeline}>{t('common:timeline')}</MenuItem>
        </Select>
      </TableCell>
      {renderSource()}
    </TableRow>
  );
};

export default ContentRow;
